import * as Yup from 'yup';

import { Button, Form, Spinner } from 'react-bootstrap';
import { Field, Formik } from 'formik';
import React, { useContext, useEffect, useState } from 'react';
import { contentfulPropTypes, onboardingPropTypes } from '../../propTypes';
import { flowSteps, recordFlowProgress } from '../../utils/recordFlowProgress';

import { ConsentContext } from '../../context/ConsentContext';
import ErrorLabel from '../../ui/ErrorLabel';
import ReactMarkdown from 'react-markdown';
import TermsModal from '../../ui/Modal/TermsModal';
import checkboxEmpty from '../../assets/svg/check-box-empty.svg';
import checkboxError from '../../assets/svg/check-box-error.svg';
import checkboxFilled from '../../assets/svg/check-box-filled.svg';
import cx from 'classnames';
import styled from 'styled-components';
import { useNavigate } from 'react-router';
import withContentfulTag from '../../utils/withContentfulTag';

const StyledHr = styled.hr`
  border-color: #afb6b9;
`;

const StyledDiv = styled.div`
  p:last-child {
    margin-bottom: 0;
  }

  ul {
    list-style: none;
    padding-left: 18px;

    li {
      margin-bottom: 8px;

      &::before {
        content: '\\2022';
        color: #ff0085;
        font-weight: bold;
        display: inline-block;
        width: 1em;
        margin-left: -1em;
        font-size: 20px;
      }
    }
  }

  &.terms-div {
    cursor: pointer;
  }

  .form-check {
    padding-left: 0;
    background: url(${checkboxEmpty}) no-repeat top left;

    input {
      display: none;
    }
    .form-check-label {
      p {
        padding-left: 32px;
      }
    }
  }

  &.selected {
    .form-check {
      background-image: url(${checkboxFilled});
    }
  }
  &.error {
    .form-check {
      background-image: url(${checkboxError});
    }
  }

  .error-label {
    margin-left: 32px;
  }
`;

const StyledButton = styled(Button)`
  margin-bottom: 5px;
`;

const TermsContainer = ({ contentful, loading, fetched }) => {
  const { consentFlow, setTermsAndConditions } = useContext(ConsentContext);
  const navigate = useNavigate();
  const [showTerms, setShowTerms] = useState(false);
  const [fullTerms, setFullTerms] = useState(undefined);

  useEffect(() => {
    const asyncRecordFlowProgress = async () => {
      await recordFlowProgress(flowSteps.TERMS_AND_CONDITIONS);
    };
    asyncRecordFlowProgress();
  }, []);

  const showFullTerms = e => {
    e.preventDefault();
    setShowTerms(true);
  };

  const submitConsent = values => {
    if (!!values.confirm) {
      const termsAndConditions = {
        page: `${contentful?.title}\n\n${contentful?.subtitle}\n\n${contentful?.body}\n\n${contentful?.bodyEnd}`,
        modal: fullTerms
          ? `${fullTerms?.title}\n\n${fullTerms?.body}`
          : 'Full terms not viewed',
      };
      setTermsAndConditions(JSON.stringify(termsAndConditions));
      navigate(`/${consentFlow}/verify-address`);
    }
  };

  const onHideTerms = () => {
    return () => setShowTerms(false);
  };

  return (
    !loading &&
    fetched && (
      <>
        {showTerms && (
          <TermsModal
            showTerms={showTerms}
            onHideTerms={onHideTerms()}
            navigate={navigate}
            setFullTerms={setFullTerms}
          />
        )}
        <p>
          Step <strong>2</strong> of <strong>3</strong>
        </p>
        <h1 className="mb-3">{contentful?.title}</h1>
        <StyledDiv>
          <ReactMarkdown>{contentful?.subtitle}</ReactMarkdown>
        </StyledDiv>
        <StyledHr />
        <StyledDiv className="text-left terms-div" onClick={showFullTerms}>
          <ReactMarkdown>{contentful?.body}</ReactMarkdown>
        </StyledDiv>
        <StyledHr />
        <Formik
          validationSchema={Yup.object().shape({
            confirm: Yup.boolean()
              .required(contentful?.utilityText)
              .oneOf([true], contentful?.utilityText),
          })}
          onSubmit={submitConsent}
          initialValues={{
            confirm: false,
          }}
          validateOnChange>
          {({ handleSubmit, isValid, isSubmitting }) => (
            <Form onSubmit={handleSubmit}>
              <Field name="confirm">
                {({ field, meta }) => (
                  <StyledDiv
                    className={`text-left ${cx({
                      selected: field.value,
                      error: !!meta.error,
                    })}`}>
                    <Form.Check
                      type="checkbox"
                      name="confirm"
                      id="confirm-check"
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      className={cx({ error: !!meta.error })}
                      label={
                        <ReactMarkdown>{contentful?.bodyEnd}</ReactMarkdown>
                      }
                    />
                    {meta.error && meta.touched && (
                      <ErrorLabel label={meta.error} />
                    )}
                  </StyledDiv>
                )}
              </Field>
              <StyledButton
                className="w-100"
                type="submit"
                variant="primary"
                disabled={isValid && isSubmitting}>
                {isValid && isSubmitting ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  'Continue'
                )}
              </StyledButton>
            </Form>
          )}
        </Formik>
      </>
    )
  );
};

TermsContainer.propTypes = {
  ...contentfulPropTypes,
  ...onboardingPropTypes,
};

export default withContentfulTag(TermsContainer, 'web_app_onboarding_terms');
